
import {Component, Prop, Vue, Watch} from 'vue-property-decorator';
import {ProjectDocument, StatsProjectDocument} from "@/interfaces/project.interfaces";
import {statsProject} from "@/axios/axios.project";
import SAccContent from "@/components/projects/dialog/SAccContent.vue";
import {UserProjectDocument} from "@/interfaces/user.project.interfaces";
import {fetchUsersProject, removeUserProject} from "@/axios/axios.user";
import {
  deleteBuffer, deleteJob, deleteProject, deleteProjectTreeNodes, deleteReleases, deleteScreen,
  deleteTestcase, deleteTestGoal, deleteTobject, deleteVariable, removeTerminal,
  allVariables, fetchAllScreens, fetchBuffers, fetchJobs,
  fetchTerminals, fetchTestcases, fetchTestGoals, fetchTools
} from "@/axios/axios.delete_project";

const emptyProgress: StatsProjectDocument = {
  users: 0, goals: 0, cases: 0, jobs: 0, screens: 0, tools: 0, buffers: 0, releases: 0, terminals: 0, variables: 0
}

@Component({
  components: {SAccContent}
})
export default class SAccProjectContentDialog extends Vue {
  // Les props
  @Prop({default: null}) private project!: ProjectDocument | null

  // Les propriétés
  private isVisible: boolean = false
  private confirmDelete: boolean = false
  private loading: boolean = false
  private stats: StatsProjectDocument | null = null
  private progress: StatsProjectDocument = {...emptyProgress}
  private deleting: boolean = false

  // Les propriétés calculées
  private get theme() {
    return this.$store.state.theme
  }

  private get projectName() {
    return this.project ? this.project.name : ''
  }

  // Les hooks
  // Les méthodes surveillées
  @Watch('isVisible')
  private async onVisible() {
    if (this.isVisible) {
      await this.loadContent()
    } else {
      this.confirmDelete = false
      this.progress = {...emptyProgress}
      this.deleting = false
    }
  }

  // Les méthodes d'instance
  private async loadContent() {
    if (this.project) {
      this.loading = true
      this.stats = await statsProject(this.project._id)
      this.loading = false
    }
  }

  private async deleteProcess() {
    if (this.project) {
      const prjid = this.project._id
      this.deleting = true
      await this.deleteUsers(prjid)
      await this.deleteGoals(prjid)
      await this.deleteCases(prjid)
      await this.deleteJobs(prjid)
      await this.deleteScreens(prjid)
      await this.deleteTools(prjid)
      await this.deleteBuffers(prjid)
      await this.deleteReleases(prjid)
      await this.deleteTerminals(prjid)
      await this.deleteVariables(prjid)
      await this.deleteProject(prjid)
      this.deleting = false
      this.isVisible = false
      this.$emit('deleted')
    }
  }

  private async deleteUsers(prjid: string) {
    for (const u of await fetchUsersProject(prjid)) {
      await removeUserProject(u._id)
      this.progress.users++
    }
  }

  private async deleteGoals(prjid: string) {
    for (const g of await fetchTestGoals(prjid)) {
      await deleteTestGoal(g._id)
      this.progress.goals++
    }
  }

  private async deleteCases(prjid: string) {
    for (const c of await fetchTestcases(prjid)) {
      await deleteTestcase(c._id)
      this.progress.cases++
    }
  }

  private async deleteJobs(prjid: string) {
    for (const j of await fetchJobs(prjid)) {
      await deleteJob(j._id)
      this.progress.jobs++
    }
  }

  private async deleteScreens(prjid: string) {
    for (const s of await fetchAllScreens(prjid)) {
      await deleteScreen(s._id)
      this.progress.screens++
    }
  }

  private async deleteTools(prjid: string) {
    for (const t of await fetchTools(prjid)) {
      await deleteTobject(t._id)
      this.progress.tools++
    }
  }

  private async deleteBuffers(prjid: string) {
    for (const b of await fetchBuffers(prjid)) {
      await deleteBuffer(b._id)
      this.progress.buffers++
    }
  }

  private async deleteReleases(prjid: string) {
    await deleteReleases(prjid)
    this.progress.releases = this.stats ? this.stats.releases : 0
  }


  private async deleteTerminals(prjid: string) {
    for (const t of await fetchTerminals(prjid)) {
      await removeTerminal(t._id)
      this.progress.terminals++
    }
  }

  private async deleteVariables(prjid: string) {
    for (const v of await allVariables(prjid)) {
      await deleteVariable(v._id)
      this.progress.variables++
    }
  }

  private async deleteProject(prjid: string) {
    await deleteProjectTreeNodes(prjid)
    await deleteProject(prjid)
  }

  // Utilisé pour simuler le processus de suppression du projet lors des tests de l'UI
  // private async deleteSome(elem: string) {
  //   if (this.stats) {
  //     const some: number = this.stats[elem]
  //     for (let n = 0; n < some; n++) {
  //       await this.timeout(50)
  //       this.progress[elem]++
  //     }
  //   }
  // }
  // private timeout = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

  // Les méthodes statiques
}
